/**
 * Media Upload Control Component
 *
 * A comprehensive media upload control that provides media selection
 * with preview, replace, and remove functionality.
 *
 * @package RedfoxThankYou
 * @since 1.0.0
 */

import './style.scss';

import { MediaUpload, MediaUploadCheck } from '@wordpress/block-editor';
import { Button } from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import { image, pencil, trash } from '@wordpress/icons';
import React from 'react';

import { MediaObject, MediaUploadControlProps } from './types';

export const MediaUploadControl: React.FC< MediaUploadControlProps > = ( {
	value,
	onChange,
	allowedTypes = [ 'image' ],
	multiple = false,
	gallery = false,
	uploadText = __( 'Select Media', 'redfox-thank-you' ),
	replaceText = __( 'Replace', 'redfox-thank-you' ),
	removeText = __( 'Remove', 'redfox-thank-you' ),
	showPreview = true,
	previewSize: _previewSize = 'medium',
	className = '',
	disabled = false,
	help,
} ) => {
	/**
	 * Handle media selection
	 */
	const handleMediaSelect = ( media: MediaObject | MediaObject[] ) => {
		if ( multiple && Array.isArray( media ) ) {
			// Handle multiple media selection (for future use)
			const mediaObjects: MediaObject[] = media.map( ( item ) => ( {
				id: item.id,
				url: item.url,
				alt: item.alt ?? '',
				title: item.title ?? '',
				description: item.description ?? '',
				caption: item.caption ?? '',
				filename: item.filename ?? '',
				mime: item.mime ?? '',
				width: item.width,
				height: item.height,
			} ) );
			onChange( mediaObjects[ 0 ] ); // For now, just use the first one
		} else if ( ! Array.isArray( media ) ) {
			// Handle single media selection
			const mediaObject: MediaObject = {
				id: media.id,
				url: media.url,
				alt: media.alt ?? '',
				title: media.title ?? '',
				description: media.description ?? '',
				caption: media.caption ?? '',
				filename: media.filename ?? '',
				mime: media.mime ?? '',
				width: media.width,
				height: media.height,
			};
			onChange( mediaObject );
		}
	};

	/**
	 * Handle media removal
	 */
	const handleMediaRemove = () => {
		onChange( undefined );
	};

	/**
	 * Get the preview URL based on size preference
	 */
	const getPreviewUrl = (): string => {
		if ( ! value?.url ) return '';

		// For now, just return the main URL
		// In a real WordPress environment, you might want to use different sizes
		return value.url;
	};

	/**
	 * Render the upload button
	 */
	const renderUploadButton = ( open: () => void ) => (
		<Button
			onClick={ open }
			icon={ image }
			variant="secondary"
			className="redfox-thank-you-media-upload-control__upload-button"
			disabled={ disabled }
		>
			<span>{ uploadText }</span>
		</Button>
	);

	/**
	 * Render the media preview with actions
	 */
	const renderMediaPreview = ( open: () => void ) => {
		// If no value at all, show upload button
		if ( ! value ) {
			return renderUploadButton( open );
		}

		// If showPreview is false, show upload button
		if ( ! showPreview ) {
			return renderUploadButton( open );
		}

		// If we have a media object, show preview/actions even if URL is empty
		return (
			<div
				className="redfox-thank-you-media-upload-control__preview"
				data-testid="media-preview"
			>
				<div
					className="redfox-thank-you-media-upload-control__preview-media"
					onClick={ open }
				>
					{ value.url && value.mime?.startsWith( 'image/' ) ? (
						<img
							src={ getPreviewUrl() }
							alt={ value.alt ?? '' }
							className="redfox-thank-you-media-upload-control__preview-image"
						/>
					) : value.url ? (
						<div className="redfox-thank-you-media-upload-control__preview-file">
							<span className="redfox-thank-you-media-upload-control__preview-filename">
								{ value.filename ??
									value.url.split( '/' ).pop() }
							</span>
						</div>
					) : (
						<div className="redfox-thank-you-media-upload-control__preview-placeholder">
							<span className="redfox-thank-you-media-upload-control__preview-no-url">
								{ __(
									'Media selected (no preview available)',
									'redfox-thank-you',
								) }
							</span>
						</div>
					) }
				</div>
				<div className="redfox-thank-you-media-upload-control__preview-actions">
					<Button
						onClick={ open }
						icon={ pencil }
						iconSize={ 18 }
						variant="secondary"
						disabled={ disabled }
					>
						<span className="screen-reader-text">
							{ replaceText }
						</span>
					</Button>
					<Button
						onClick={ handleMediaRemove }
						icon={ trash }
						iconSize={ 18 }
						variant="secondary"
						isDestructive
						disabled={ disabled }
					>
						<span className="screen-reader-text">
							{ removeText }
						</span>
					</Button>
				</div>
			</div>
		);
	};

	return (
		<div className={ `redfox-thank-you-media-upload ${ className }` }>
			<MediaUploadCheck>
				<MediaUpload
					onSelect={ handleMediaSelect }
					allowedTypes={ allowedTypes }
					value={ value?.id }
					multiple={ multiple }
					gallery={ gallery }
					render={ ( { open }: { open: () => void } ) => (
						<div className="redfox-thank-you-media-upload-control__content">
							{ renderMediaPreview( open ) }
							{ help && (
								<p className="redfox-thank-you-media-upload-control__help">
									{ help }
								</p>
							) }
						</div>
					) }
				/>
			</MediaUploadCheck>
		</div>
	);
};
